package com.tencent.kuikly.core.render.web.runtime.web.expand.components.list

import com.tencent.kuikly.core.render.web.collection.array.add
import com.tencent.kuikly.core.render.web.processor.KuiklyProcessor
import com.tencent.kuikly.core.render.web.const.KRAttrConst
import com.tencent.kuikly.core.render.web.const.KRCssConst
import com.tencent.kuikly.core.render.web.const.KREventConst
import com.tencent.kuikly.core.render.web.const.KRListConst
import com.tencent.kuikly.core.render.web.const.KRParamConst
import com.tencent.kuikly.core.render.web.const.KRStyleConst
import com.tencent.kuikly.core.render.web.expand.components.list.KRListViewContentInset
import com.tencent.kuikly.core.render.web.ktx.KuiklyRenderCallback
import com.tencent.kuikly.core.render.web.ktx.kuiklyDocument
import com.tencent.kuikly.core.render.web.ktx.kuiklyWindow
import com.tencent.kuikly.core.render.web.runtime.dom.element.ElementType
import com.tencent.kuikly.core.render.web.runtime.dom.element.IListElement
import com.tencent.kuikly.core.render.web.scheduler.KuiklyRenderCoreContextScheduler
import com.tencent.kuikly.core.render.web.utils.Log
import org.w3c.dom.AUTO
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.SMOOTH
import org.w3c.dom.ScrollBehavior
import org.w3c.dom.ScrollToOptions
import org.w3c.dom.TouchEvent
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent
import org.w3c.dom.events.WheelEvent
import org.w3c.dom.get
import kotlin.js.json
import kotlin.math.abs
import kotlin.math.absoluteValue

/**
 * Web host abstract List element implementation
 */
class H5ListView : IListElement {
    // Scroll container element
    private val listEle = kuiklyDocument.createElement(ElementType.DIV).apply {
        // By default, allow scrolling in vertical direction. To hide scrollbars,
        // add 'list-no-scrollbar' class to the element
        this.unsafeCast<HTMLDivElement>().style.apply {
            // Due to bounce effect on iOS, non-scrolling direction should be set to "hidden"
            overflowX = KRStyleConst.OVERFLOW_HIDDEN
            overflowY = KRStyleConst.OVERFLOW_SCROLL
        }
        this.classList.add(KRListConst.IS_LIST)
    }
    // Scroll end event listener
    private var scrollEndEventTimer: Int = 0
    // Scroll offset Map
    private var offsetMap = mutableMapOf<String, Any>()
    // Starting horizontal scroll offset
    private var startX = 0f
    // Starting vertical scroll offset
    private var startY = 0f
    // Starting vertical touch position
    private var touchStartY = 0f
    // Current vertical touch position
    private var touchEndY = 0f
    // Starting horizontal touch position
    private var touchStartX = 0f
    // Current horizontal touch position
    private var touchEndX = 0f
    // Whether scrolling is enabled
    internal var scrollEnabled = true
        private set
    // Whether to show scrollbar
    private var showScrollerBar = true
    // Scroll direction
    private var scrollDirection = KRListConst.SCROLL_DIRECTION_COLUMN
    // Actual calculated scroll direction
    private var calculateDirection = KRListConst.SCROLL_DIRECTION_NONE
    // Whether currently dragging
    private var isDragging = 0
    // Whether paging is enabled
    var pagingEnabled = false
        private set
    // enable bounce effect, support Android Webview 63+ && iOS Safari 16+
    private var bounceEnabled = false
    // enable nest scroll effect
    var nestScrollEnabled = false
        private set
    // Whether in pre-pull-down state
    private var isPrePullDown = false
    // Pull-to-refresh height
    private var canPullRefreshHeight = 0f
    // Whether it contains pull-to-refresh child node
    private var hasRefreshChild = false
    // Scroll distance threshold
    private val scrollThreshold = KRListConst.SCROLL_THRESHOLD
    // Whether in scrolling state
    private var isScrolling = ScrollingAxis.NONE
    // Decide whether the interaction should be treated as a click
    private var clickDetectionTimer: Int? = null
    // Delay invoking the single-click callback so a possible second click can be detected
    private var singleClickConfirmTimer: Int? = null
    // Whether it's a click event
    private var isClickEvent = false
    private var touchStartTime: Double = 0.0
    // Whether the wheel is rolling
    private var isWheelRolling = false
    // Whether the wheel is stopped
    private var wheelStopTimer: Int? = null
    // Count of clicks on the current element, used to determine whether it's a double click
    private var clickCount = 0

    // Set by [prepareForComposeReuse]; the next [setContentOffset] will proactively fire a
    // scroll event even if the underlying scroll position is unchanged. This compensates
    // for the browser/miniapp behavior of not dispatching `scroll` on no-op `scrollTo`.
    private var pendingFireScrollForReuse: Boolean = false

    // real html element
    override var ele: HTMLElement = listEle.unsafeCast<HTMLElement>()

    init {
        ele.asDynamic().listView = this
    }

    // Scroll callback
    override var scrollEventCallback: KuiklyRenderCallback? = null
    // Drag begin callback
    override var dragBeginEventCallback: KuiklyRenderCallback? = null
    // Drag end callback
    override var dragEndEventCallback: KuiklyRenderCallback? = null
    // Will drag end callback
    override var willDragEndEventCallback: KuiklyRenderCallback? = null
    // Scroll end callback
    override var scrollEndEventCallback: KuiklyRenderCallback? = null
    // Click callback
    override var clickEventCallback: KuiklyRenderCallback? = null

    override var doubleClickEventCallback: KuiklyRenderCallback? = null
    
    // Whether this list has a pull-to-refresh child
    override var hasPullToRefresh: Boolean = false

    var listPagingHelper: H5ListPagingHelper = H5ListPagingHelper(ele, this)
        private set
    var nestScrollHelper: H5NestScrollHelper = H5NestScrollHelper(ele, this)
        internal set
    var pcScrollHelper: H5ListPCScrollHelper = H5ListPCScrollHelper(ele, this, this)
        private set

    /**
     * Set whether listView can scroll
     */
    override fun setScrollEnable(params: Any): Boolean {
        // Set the switch for whether scrolling is enabled
        scrollEnabled = params.unsafeCast<Int>() == KRListConst.ENABLED_FLAG
        // Set scrolling
        ele.style.apply {
            if (scrollDirection == KRListConst.SCROLL_DIRECTION_COLUMN) {
                overflowY = if (scrollEnabled) KRStyleConst.OVERFLOW_SCROLL else KRStyleConst.OVERFLOW_HIDDEN
                overflowX = KRStyleConst.OVERFLOW_HIDDEN
            } else {
                overflowX = if (scrollEnabled) KRStyleConst.OVERFLOW_SCROLL else KRStyleConst.OVERFLOW_HIDDEN
                overflowY = KRStyleConst.OVERFLOW_HIDDEN
            }
        }
        return true
    }

    override fun setBounceEnable(params: Any): Boolean {
        bounceEnabled = params.unsafeCast<Int>() == KRListConst.ENABLED_FLAG
        listPagingHelper.bounceEnabled = bounceEnabled
        // Apply overscroll-behavior to current scroll axis so non-paging mode is also controlled.
        // Browser support: Android WebView 63+, iOS Safari 16+. Lower versions silently ignore it.
        applyOverscrollBehavior()
        return true
    }

    /**
     * Sync `overscroll-behavior-x/y` with current scrollDirection and bounceEnabled.
     * - scroll axis: `auto` when bounceEnabled, otherwise `none` (disable native bounce / pull-to-refresh)
     * - cross axis: keep `auto` (no extra constraint)
     */
    private fun applyOverscrollBehavior() {
        val scrollAxisValue = if (bounceEnabled) OVERSCROLL_AUTO else OVERSCROLL_NONE
        ele.style.apply {
            if (scrollDirection == KRListConst.SCROLL_DIRECTION_COLUMN) {
                setProperty(OVERSCROLL_BEHAVIOR_Y, scrollAxisValue)
                setProperty(OVERSCROLL_BEHAVIOR_X, OVERSCROLL_AUTO)
            } else {
                setProperty(OVERSCROLL_BEHAVIOR_X, scrollAxisValue)
                setProperty(OVERSCROLL_BEHAVIOR_Y, OVERSCROLL_AUTO)
            }
        }
    }

    override fun setNestedScroll(propValue: Any): Boolean {
        nestScrollEnabled = true
        nestScrollHelper.setNestedScroll(propValue)
        return true
    }

    /**
     * Set whether to enable paging
     */
    override fun setPagingEnable(params: Any): Boolean {
        // Whether to enable paging
        pagingEnabled = params.unsafeCast<Int>() == KRListConst.ENABLED_FLAG
        return true
    }

    /**
     * Set the scroll direction of listView, 1 for horizontal, 0 for vertical
     */
    override fun setScrollDirection(params: Any): Boolean {
        val direction = if (params.unsafeCast<Int>() == KRListConst.ENABLED_FLAG) {
            KRListConst.SCROLL_DIRECTION_ROW
        } else {
            KRListConst.SCROLL_DIRECTION_COLUMN
        }
        // Set scroll direction
        ele.style.apply {
            if (direction == KRListConst.SCROLL_DIRECTION_COLUMN) {
                overflowX = KRStyleConst.OVERFLOW_HIDDEN
                overflowY = KRStyleConst.OVERFLOW_SCROLL
            } else {
                overflowX = KRStyleConst.OVERFLOW_SCROLL
                overflowY = KRStyleConst.OVERFLOW_HIDDEN
            }
        }
        scrollDirection = direction
        listPagingHelper.scrollDirection = scrollDirection
        nestScrollHelper.scrollDirection = scrollDirection
        // Re-apply overscroll-behavior to the new scroll axis
        applyOverscrollBehavior()
        return true
    }

    /**
     * Check if it contains pull-to-refresh child node
     */
    private fun checkHasRefreshChild(): Boolean {
        return hasPullToRefresh
    }

    override fun updateOffsetMap(offsetX: Float, offsetY: Float, isDragging: Int): MutableMap<String, Any> {
        offsetMap[KRParamConst.OFFSET_X] = offsetX
        offsetMap[KRParamConst.OFFSET_Y] = offsetY
        offsetMap[KRParamConst.VIEW_WIDTH] = ele.offsetWidth
        offsetMap[KRParamConst.VIEW_HEIGHT] = ele.offsetHeight
        offsetMap[KRParamConst.CONTENT_WIDTH] = ele.scrollWidth
        offsetMap[KRParamConst.CONTENT_HEIGHT] = ele.scrollHeight
        offsetMap[KRParamConst.IS_DRAGGING] = isDragging
        return offsetMap
    }

    internal fun handleTouchStart(event: Event, isMouseEvent: Boolean = false) {
        Log.trace(LOG_SCROLL_EVENT_BEGIN)
        // Set as dragging
        isDragging = 1
        // Clear pull-to-refresh height
        canPullRefreshHeight = 0f
        // Check if it contains pull-to-refresh child node
        hasRefreshChild = checkHasRefreshChild()
        // Reset scrolling state
        isScrolling = ScrollingAxis.NONE
        if (isMouseEvent) pcScrollHelper.handleMouseDown(event as MouseEvent)
        // Get horizontal and vertical offset of the element during scroll event
        val offsetX = ele.scrollLeft.toFloat()
        val offsetY = ele.scrollTop.toFloat()
        // Record scrollbar position at start of sliding
        startX = offsetX
        startY = offsetY
        // Starting drag position map
        val eventsParams = event.getEventParams()
        // Record starting vertical drag position
        touchStartY = eventsParams[KRParamConst.Y].unsafeCast<Float>()
        // Record starting horizontal drag position
        touchStartX = eventsParams[KRParamConst.X].unsafeCast<Float>()
        // Current vertical offset of the list
        offsetMap[KRParamConst.OFFSET_X] = offsetX
        // Current horizontal offset of the list
        offsetMap[KRParamConst.OFFSET_Y] = offsetY
        val offsetMap = updateOffsetMap(offsetX, offsetY, isDragging)
        // If current scroll distance is 0, and not a PageList paging component, enter pre-pull-down state
        isPrePullDown = offsetY == 0f && !pagingEnabled

        // Event callback
        dragBeginEventCallback?.invoke(offsetMap)
    }

    private fun handleMoveCommon(event: Event) {
        // Need to check if it contains pull-to-refresh component, if not, don't process todo fixme
        val eventsParams = event.getEventParams()
        var deltaY = eventsParams[KRParamConst.Y] as Float - touchStartY
        var deltaX = eventsParams[KRParamConst.X] as Float - touchStartX
        var absDeltaY = abs(deltaY)
        var absDeltaX = abs(deltaX)

        // If not yet in scrolling state, determine scroll direction, once determined don't change
        if (isScrolling == ScrollingAxis.NONE) {
            if (absDeltaY > scrollThreshold && absDeltaY > absDeltaX) {
                // Vertical scrolling
                isScrolling = ScrollingAxis.VERTICAL
            } else if (absDeltaX > scrollThreshold && absDeltaX > absDeltaY) {
                // Horizontal scrolling
                isScrolling = ScrollingAxis.HORIZONTAL
            }
        }
        if ((scrollDirection == KRListConst.SCROLL_DIRECTION_COLUMN && isScrolling == ScrollingAxis.VERTICAL) ||
            (scrollDirection == KRListConst.SCROLL_DIRECTION_ROW && isScrolling == ScrollingAxis.HORIZONTAL)) {
            // Scroll direction matches set direction, prevent bubbling to avoid affecting parent node's scroll events
            event.stopPropagation()
        }
        // If current scroll distance is 0, starting to drag down, contains pull-to-refresh child node,
        // and is vertical scrolling, handle pull-to-refresh logic, deltaY > 0 means pulling down
        if (isPrePullDown && deltaY > 0 && hasRefreshChild && isScrolling == ScrollingAxis.VERTICAL) {
            // Set end position before drag ends
            touchEndY = eventsParams[KRParamConst.Y].unsafeCast<Float>()
            // Set element's translate
            val contentEle = ele.firstElementChild.unsafeCast<HTMLElement?>()
            contentEle?.style?.transform = buildTranslateY(deltaY)
            val offsetMap = updateOffsetMap(ele.scrollLeft.toFloat(), -deltaY, isDragging)
            // Notify
            scrollEventCallback?.invoke(offsetMap)
        }
    }

    private fun handleTouchMove(it: TouchEvent) {
        handleMoveCommon(it)
    }

    internal fun handleTouchEnd() {
        isDragging = 0
        // Get horizontal and vertical offset of the element during scroll event
        val offsetX = ele.scrollLeft.toFloat()
        var offsetY = ele.scrollTop.toFloat()
        if (isPrePullDown) {
            // Special handling for pull-to-refresh
            val deltaY = touchEndY - touchStartY
            if (canPullRefreshHeight == 0f) {
                // If at pull-to-refresh release but not reaching pull-to-refresh position,
                // need to restore contentInset and scrolling
                val contentEle = ele.firstElementChild.unsafeCast<HTMLElement?>()
                contentEle?.style?.transform = KRListConst.TRANSFORM_RESET
                // Handle extreme sliding in static sliding scenarios
                if (scrollEnabled) {
                    if (scrollDirection == KRListConst.SCROLL_DIRECTION_COLUMN) {
                        ele.style.overflowY = KRStyleConst.OVERFLOW_SCROLL
                    } else {
                        ele.style.overflowX = KRStyleConst.OVERFLOW_SCROLL
                    }
                }

                // remove transform attribute after transform end
                kuiklyWindow.setTimeout({
                    contentEle?.style?.transform = KRCssConst.EMPTY_STRING
                }, KRListConst.IMMEDIATE_TIMEOUT)
            } else if (deltaY > canPullRefreshHeight) {
                val contentEle = ele.firstElementChild.unsafeCast<HTMLElement?>()
                contentEle?.style?.transition = buildTransition()
                // If at pull-to-refresh release and exceeding pull-to-refresh height,
                // need to bounce back to pull-to-refresh height before refreshing
                contentEle?.style?.transform = buildTranslateY(canPullRefreshHeight)
            }
            // If current scroll distance is 0 and starting to drag down, handle pull-to-refresh logic,
            // deltaY > 0 means pulling down
            if (deltaY > 0) {
                // Result is negative
                offsetY = -deltaY
            }
        }
        // Current vertical offset of the list
        offsetMap[KRParamConst.OFFSET_X] = offsetX
        // Current horizontal offset of the list
        offsetMap[KRParamConst.OFFSET_Y] = offsetY
        val offsetMap = updateOffsetMap(offsetX, offsetY, isDragging)
        // Event callback
        willDragEndEventCallback?.invoke(offsetMap)
        dragEndEventCallback?.invoke(offsetMap)
        scrollEventCallback?.invoke(offsetMap)
    }

    private fun handleTouchScroll() {
        // Get horizontal and vertical offset of the element during scroll event
        val offsetMap = updateOffsetMap(ele.scrollLeft.toFloat(), ele.scrollTop.toFloat(), isDragging)
        // Callback with offset
        scrollEventCallback?.invoke(offsetMap)
    }

    /**
     * 执行 click、doubleClick 回调
     */
    private fun invokeClickCallback(event: Event, isDoubleClick: Boolean) {
        val clickOffsetMap = if (event.isTouchEventOrNull() != null) {
            val touch = event.unsafeCast<TouchEvent>().changedTouches[0] ?: return
            val x = touch.clientX
            val y = touch.clientY
            // Calculate element position
            val position = ele.getBoundingClientRect()
            // Element distance from left side of page
            val eleX = position.left
            // Element distance from top of page
            val eleY = position.top
            // Calculate offset
            val offsetX = x.toDouble() - eleX
            val offsetY = y.toDouble() - eleY
            mapOf(KRParamConst.X to offsetX, KRParamConst.Y to offsetY)
        } else {
            mapOf(
                KRParamConst.X to event.unsafeCast<MouseEvent>().offsetX,
                KRParamConst.Y to event.unsafeCast<MouseEvent>().offsetY
            )
        }

        if (isDoubleClick) {
            doubleClickEventCallback?.invoke(clickOffsetMap)
        } else {
            clickEventCallback?.invoke(clickOffsetMap)
        }
    }

    /**
     * 处理 click、doubleClick 事件
     */
    internal fun handleClickEvent(it: Event) {
        // If it is considered as a click event
        // Record the current click count
        clickCount++
        // Whether the double-click event is registered
        if (!ele.asDynamic().hasDoubleClickListener as Boolean) {
            // If no double-click event is registered，invoke the click callback
            invokeClickCallback(it, false)
            // Reset the click count
            clickCount = 0
            return
        } else {
            // If a double click handler is registered
            if (clickCount == KRListConst.DOUBLE_CLICK_COUNT) {
                // Clear the timer to prevent the click callback from being invoked afterward
                val timer = singleClickConfirmTimer
                if (timer != null) {
                    kuiklyWindow.clearTimeout(timer)
                    singleClickConfirmTimer = null
                }
                // Reset the click count
                clickCount = 0
                // Invoke the double-click callback
                invokeClickCallback(it, true)
            } else {
                // If the timer exists , clear it (reset the timing)
                val prevTimer = singleClickConfirmTimer
                if (prevTimer != null) kuiklyWindow.clearTimeout(prevTimer)
                singleClickConfirmTimer = kuiklyWindow.setTimeout({
                    // If the double click callback is not triggered within timeout, invoke the click callback
                    // When double click callback triggered, the timer will be cleared
                    invokeClickCallback(it, false)
                    // Clear the timer
                    singleClickConfirmTimer = null
                    // Reset the click count
                    clickCount = 0
                }, KRListConst.DOUBLE_CLICK_TIMEOUT)
            }
        }
    }

    // Helper methods for PC scroll helper to access click state
    internal fun isClickEvent(): Boolean = isClickEvent
    internal fun setClickEvent(value: Boolean) { isClickEvent = value }
    internal fun cancelClickDetectionTimer() {
        clickDetectionTimer?.let {
            kuiklyWindow.clearTimeout(it)
            clickDetectionTimer = null
        }
    }

    /**
     * Bind scroll-related events
     */
    override fun setScrollEvent() {
        // If it is a pointing device with limited precision, listen for touch events.
        if (kuiklyWindow.matchMedia(KRListConst.POINTER_COARSE_QUERY).matches) {
            // Start dragging
            ele.addEventListener(KREventConst.TOUCH_START, {
                isClickEvent = true
                // If the mousemove event is not triggered, it will be considered a click event
                clickDetectionTimer = kuiklyWindow.setTimeout({
                    isClickEvent = true
                }, KRListConst.CLICK_DETECTION_TIMEOUT_TOUCH)
                if (pagingEnabled) {
                    if (!scrollEnabled) return@addEventListener
                    listPagingHelper.handlePagerTouchStart(it as TouchEvent)
                    return@addEventListener
                }
                if (nestScrollEnabled) {
                    nestScrollHelper.handleNestScrollTouchStart(it as TouchEvent)
                    return@addEventListener
                }
                handleTouchStart(it as TouchEvent)
            }, json(KRAttrConst.PASSIVE to true))

            // Move event
            ele.addEventListener(KREventConst.TOUCH_MOVE, {
                clickDetectionTimer?.let {
                    kuiklyWindow.clearTimeout(it)
                    clickDetectionTimer = null
                }
                isClickEvent = false
                if (pagingEnabled) {
                    if (!scrollEnabled) return@addEventListener
                    listPagingHelper.handlePagerTouchMove(it as TouchEvent)
                    return@addEventListener
                }
                if (nestScrollEnabled) {
                    nestScrollHelper.handleNestScrollTouchMove(it as TouchEvent)
                    return@addEventListener
                }
                handleTouchMove(it as TouchEvent)
            }, json(KRAttrConst.PASSIVE to (!pagingEnabled && !nestScrollEnabled)))

            // End dragging
            ele.addEventListener(KREventConst.TOUCH_END, {
                if (isClickEvent) {
                    handleClickEvent(it)
                    return@addEventListener
                }
                if (pagingEnabled) {
                    listPagingHelper.handlePagerTouchEnd(it as TouchEvent)
                    return@addEventListener
                }
                if (nestScrollEnabled) {
                    nestScrollHelper.handleNestScrollTouchEnd(it as TouchEvent)
                    return@addEventListener
                }
                handleTouchEnd()
            }, json(KRAttrConst.PASSIVE to true))
        }

        // If it is a precise pointing device, listen for mouse events.
        if (kuiklyWindow.matchMedia(KRListConst.POINTER_FINE_QUERY).matches) {
            ele.addEventListener(KREventConst.MOUSE_DOWN, { event ->
                event as MouseEvent
                // Only left button
                if (event.button != KRListConst.LEFT_MOUSE_BUTTON) return@addEventListener
                pcScrollHelper.isMouseDown = true
                // Reset click flag
                isClickEvent = true
                // If the mousemove event is not triggered, it will be considered a click event
                clickDetectionTimer = kuiklyWindow.setTimeout({
                    isClickEvent = true
                }, KRListConst.CLICK_DETECTION_TIMEOUT_MOUSE)
                // Save the current element
                PCListScrollHandler.mouseDownEleIds.add(ele.id)
                // Filter elements belonging to ListView
                PCListScrollHandler.filterScrollElementIds()
                // Initialize canScroll state
                pcScrollHelper.initCanScroll(showScrollerBar)
                if (pagingEnabled) {
                    if (!scrollEnabled) return@addEventListener
                    listPagingHelper.handlePagerMouseDown(event)
                    return@addEventListener
                }
                if (nestScrollEnabled) {
                    nestScrollHelper.handleNestScrollMouseDown(event)
                    return@addEventListener
                }
                handleTouchStart(event, true)
            }, json(KRAttrConst.PASSIVE to true))

            // Prevent text selection
            if (KuiklyProcessor.preventDefaultSelect) {
                ele.addEventListener(KREventConst.SELECT_START, {
                    it.preventDefault()
                })
            }
            // Prevent image drag
            if (KuiklyProcessor.preventDefaultDrag) {
                ele.addEventListener(KREventConst.DRAG_START, {
                    it.preventDefault()
                })
            } else {
                // Defensive fallback: when native HTML5 drag is allowed (e.g. user disabled
                // [preventDefaultDrag] / [preventDefaultDragAndSelect] to support text copy),
                // a `dragstart` will cause the browser to stop dispatching mousemove/mouseup,
                // which would leave `pcScrollHelper.isMouseDown` stuck as true and the list
                // would keep following the cursor until the next click. So we proactively
                // finalize the PC scroll state when a drag starts.
                ele.addEventListener(KREventConst.DRAG_START, { evt ->
                    // dragstart inherits from MouseEvent.
                    val mouseEvt = evt as MouseEvent
                    pcScrollHelper.cancelMouseInteraction(mouseEvt)
                    PCListScrollHandler.cancelMouseInteraction(mouseEvt)
                })
            }
        }
        ele.addEventListener(KREventConst.WHEEL, { event ->
            // Handle paging mode with wheel event
            event as WheelEvent
            if (pagingEnabled) {
                if (!scrollEnabled) return@addEventListener
                var eps = 1.0; // depending on device sensitivity
                val isVerticalScroll = event.deltaY.absoluteValue > event.deltaX.absoluteValue + eps
                val isHorizontalScroll = event.deltaX.absoluteValue > event.deltaY.absoluteValue + eps
                val isWheelMatchDirection = (isVerticalScroll && scrollDirection == KRListConst.SCROLL_DIRECTION_COLUMN)
                        || (isHorizontalScroll && scrollDirection == KRListConst.SCROLL_DIRECTION_ROW)
                if (isWheelMatchDirection) {
                    listPagingHelper.handlePagerWheel(event)
                }
                return@addEventListener
            }

            // Normal scroll mode
            if (!isWheelRolling) {
                isWheelRolling = true
                // 滚动条触发尾部刷新（FooterRefreshView需要拖拽过一次才能进行加载更多）
                handleTouchStart(event)
            }
            // When the wheel is rolled, the previous timer is cleared and a new timer is set.
            wheelStopTimer?.let {
                kuiklyWindow.clearTimeout(it)
            }
            wheelStopTimer = kuiklyWindow.setTimeout({
                // The callback is executed when the timer expires.
                isWheelRolling = false
                handleTouchEnd()
            }, KRListConst.WHEEL_STOP_TIMEOUT)
        })
        // Scroll event
        ele.addEventListener(KREventConst.SCROLL, {
            if (pagingEnabled) {
                // In paging mode, no need to trigger scroll
                // Calculate offset through touchmove and touchend,
                // and callback scroll event to upper layer for processing
                return@addEventListener
            }
            if (nestScrollEnabled) {
                nestScrollHelper.handleNestScrollTouchScroll(it)
                return@addEventListener
            }
            handleTouchScroll()
        }, json(KRAttrConst.PASSIVE to false))
    }

    /**
     * Set scroll end callback event
     */
    override fun setScrollEndEvent() {
        // scroll end event not available, simulate through other means
        ele.addEventListener(KREventConst.SCROLL, {
            // Clear existing timer first
            if (scrollEndEventTimer > 0) {
                kuiklyWindow.clearTimeout(scrollEndEventTimer)
            }
            // Reset timer
            scrollEndEventTimer = kuiklyWindow.setTimeout({
                // Get horizontal and vertical offset of the element during scroll event
                var offsetMap = updateOffsetMap(ele.scrollLeft.toFloat(), ele.scrollTop.toFloat(), isDragging)
                scrollEndEventCallback?.invoke(offsetMap)
            }, KRListConst.SCROLL_END_OVERTIME)
        }, json(KRAttrConst.PASSIVE to true))
    }

    /**
     * Scroll element to specified position
     */
    override fun setContentOffset(params: String?) {
        // Don't process if no parameters
        if (params === null) {
            return
        }

        // Format scroll parameters
        val contentOffsetSplits = params.split(KRCssConst.BLANK_SEPARATOR)
        val offsetX = contentOffsetSplits[0].toFloat()
        val offsetY = contentOffsetSplits[1].toFloat()
        val animate = contentOffsetSplits[2] == KRListConst.ANIMATE_FLAG

        if (offsetX.isNaN() || offsetY.isNaN()) {
            // Position parameters abnormal, return
            return
        }
        if (pagingEnabled) {
            listPagingHelper.setContentOffset(offsetX, offsetY, animate)
            // listPagingHelper.setContentOffset already invokes scrollEventCallback synchronously,
            // but during Compose reuse the upper-layer scrollEventCallback may not yet be registered
            // when this method runs (callback is registered later via listenScrollEvent in
            // LaunchedEffect). Therefore we still need to async re-fire so the upper layer can
            // clear ignoreScrollOffset.
            if (pendingFireScrollForReuse) {
                pendingFireScrollForReuse = false
                kuiklyWindow.setTimeout({
                    val cb = scrollEventCallback ?: return@setTimeout
                    val map = updateOffsetMap(
                        abs(listPagingHelper.currentTranslateX),
                        abs(listPagingHelper.currentTranslateY),
                        isDragging,
                    )
                    cb.invoke(map)
                }, KRListConst.IMMEDIATE_TIMEOUT)
            }
            return
        }
        // Scroll to specified distance
        ele.scrollTo(
            ScrollToOptions(
                offsetX.toDouble(),
                offsetY.toDouble(),
                if (animate) ScrollBehavior.SMOOTH else ScrollBehavior.AUTO
            )
        )
        // After Compose DSL reuse, the upper layer sets `ignoreScrollOffset` and expects the
        // next setContentOffset to fire a scroll event so the flag can be cleared. However,
        // when the target offset equals the current scrollTop/scrollLeft, browsers won't
        // dispatch a `scroll` event at all. To match iOS/Android semantics ("setContentOffset
        // always triggers a scroll callback"), proactively fire one async scroll event.
        if (pendingFireScrollForReuse) {
            pendingFireScrollForReuse = false
            kuiklyWindow.setTimeout({
                val cb = scrollEventCallback ?: return@setTimeout
                val map = updateOffsetMap(ele.scrollLeft.toFloat(), ele.scrollTop.toFloat(), isDragging)
                cb.invoke(map)
            }, KRListConst.IMMEDIATE_TIMEOUT)
        }
    }

    /**
     * Clear transient state for Compose DSL reuse.
     *
     * The actual "reset" web side needs is much smaller than native (no native cell pool here);
     * the critical part is to make sure the *next* [setContentOffset] still fires a scroll
     * event even if scrollTop/scrollLeft do not change, so that the upper-layer
     * `ignoreScrollOffset` flag can be cleared.
     */
    override fun prepareForComposeReuse() {
        pendingFireScrollForReuse = true
    }

    /**
     * Set whether listView needs scrollbars
     */
    override fun setShowScrollIndicator(params: Any): Boolean {
        // Whether to show scrollbars
        showScrollerBar = params.unsafeCast<Int>() == KRListConst.ENABLED_FLAG
        if (showScrollerBar) {
            // Remove the class that hides scrollbars
            ele.classList.remove(KRListConst.NO_SCROLL_BAR_CLASS)
        } else {
            // Add the class that hides scrollbars
            ele.classList.add(KRListConst.NO_SCROLL_BAR_CLASS)
        }
        return true
    }

    /**
     * Set content inset with animation
     */
    override fun setContentInset(params: String?) {
        // Inset value to set
        val contentInsetString = params ?: return
        // Format inset value
        val contentInset = KRListViewContentInset(contentInsetString)
        // Complete setting asynchronously
        KuiklyRenderCoreContextScheduler.scheduleTask(KRListConst.IMMEDIATE_TIMEOUT) {
            // Use animation to set inset value if needed
            val contentEle = ele.firstElementChild.unsafeCast<HTMLElement?>()
            contentEle?.style?.transition = if (contentInset.animate) {
                buildTransition()
            } else {
                KRCssConst.EMPTY_STRING
            }
            // Set the value to complete
            contentEle?.style?.transform = buildTranslate(contentInset.left, contentInset.top)
        }
    }

    /**
     * Set inner padding when drag ends, i.e., translateX and Y values
     */
    override fun setContentInsetWhenEndDrag(params: String?) {
        // Inset value to set
        val contentInsetString = params ?: return
        // Format inset value
        val contentInset = KRListViewContentInset(contentInsetString)
        // Transform content to set
        val transform = buildTranslate(contentInset.left, contentInset.top)
        if (contentInset.top == 0f) {
            // Restore listView to scrollable
            if (scrollDirection == KRListConst.SCROLL_DIRECTION_COLUMN) {
                ele.style.overflowY = KRStyleConst.OVERFLOW_SCROLL
                ele.style.overflowX = KRStyleConst.OVERFLOW_HIDDEN
            } else {
                ele.style.overflowX = KRStyleConst.OVERFLOW_SCROLL
                ele.style.overflowY = KRStyleConst.OVERFLOW_HIDDEN
            }
            // When top > 0, it sets the terminal listView inset height when terminal pull-to-refresh,
            // web doesn't support pull bounce by default,
            // so this value is not processed, only handle the value when preparing for pull-to-refresh
            KuiklyRenderCoreContextScheduler.scheduleTask(KRListConst.BOUND_BACK_DURATION.toInt()) {
                // Clear animation
                val contentEle = ele.firstElementChild.unsafeCast<HTMLElement?>()
                contentEle?.style?.transition = KRCssConst.EMPTY_STRING
                // Delay setting inset value until pull-down animation completes
                contentEle?.style?.transform = if (contentInset.left == 0f && contentInset.top == 0f) {
                    KRCssConst.EMPTY_STRING
                } else {
                    transform
                }
            }
        } else {
            // This indicates it has been pulled down to a position where it can refresh,
            // record the pull-to-refresh position
            canPullRefreshHeight = contentInset.top
        }
    }


    /**
     * Clear existing timers and resources when component is destroyed
     */
    override fun destroy() {
        // Clear all timers
        if (scrollEndEventTimer > 0) {
            kuiklyWindow.clearTimeout(scrollEndEventTimer)
            scrollEndEventTimer = 0
        }
        
        clickDetectionTimer?.let {
            kuiklyWindow.clearTimeout(it)
        }
        clickDetectionTimer = null
        
        singleClickConfirmTimer?.let {
            kuiklyWindow.clearTimeout(it)
        }
        singleClickConfirmTimer = null
        
        wheelStopTimer?.let {
            kuiklyWindow.clearTimeout(it)
        }
        wheelStopTimer = null
        
        // Clear helper resources (timers and requestAnimationFrame)
        listPagingHelper.destroy()
        nestScrollHelper.destroy()
    }

    companion object {
        // Log messages
        private const val LOG_SCROLL_EVENT_BEGIN = "scroll direction event begin"

        // CSS property names
        private const val TRANSFORM_PROPERTY = "transform"

        // CSS overscroll-behavior property names and values, used to control native bounce/pull-to-refresh
        private const val OVERSCROLL_BEHAVIOR_X = "overscroll-behavior-x"
        private const val OVERSCROLL_BEHAVIOR_Y = "overscroll-behavior-y"
        private const val OVERSCROLL_AUTO = "auto"
        private const val OVERSCROLL_NONE = "none"

        // Helper functions for building CSS values
        private fun buildTranslateY(y: Any) = "translate(0, $y${KRStyleConst.PX_SUFFIX})"
        private fun buildTranslate(x: Any, y: Any) =
            "translate($x${KRStyleConst.PX_SUFFIX}, $y${KRStyleConst.PX_SUFFIX})"
        private fun buildTransition() =
            "$TRANSFORM_PROPERTY ${KRListConst.BOUND_BACK_DURATION}${KRStyleConst.MS_SUFFIX} ${KRStyleConst.EASE_IN}"
    }
}

enum class KRNestedScrollMode(val value: String) {
    SELF_ONLY("SELF_ONLY"),
    SELF_FIRST("SELF_FIRST"),
    PARENT_FIRST("PARENT_FIRST"),
}

enum class KRNestedScrollState(val value: String) {
    CAN_SCROLL("CAN_SCROLL"),
    SCROLL_BOUNDARY("SCROLL_BOUNDARY"),
    CANNOT_SCROLL("CANNOT_SCROLL"),
}
